import { useEffect, useState } from 'react';

import Text from '@cartwhl/ui/base/Text';

interface TypewriterHeadlineProps {
  headlines: string[];
  cursorVariant?: 'thin' | 'thick';
}

const TYPE_SPEED_MS = 33;
const DELETE_SPEED_MS = 45;
const HEADLINE_PAUSE_MS = 3000;

export default function TypewriterHeadline({
  headlines,
  cursorVariant = 'thick',
}: TypewriterHeadlineProps) {
  const [activeHeadline, setActiveHeadline] = useState(0);
  const [typedHeadline, setTypedHeadline] = useState('');
  const [isDeletingHeadline, setIsDeletingHeadline] = useState(false);

  useEffect(() => {
    if (headlines.length === 0) {
      return;
    }

    if (headlines.length === 1 && activeHeadline !== 0) {
      setActiveHeadline(0);
      return;
    }

    const fullHeadline = headlines[activeHeadline] ?? '';
    const isCurrentHeadlineFullyTyped = typedHeadline === fullHeadline;
    const isCurrentHeadlineRemoved = typedHeadline.length === 0;
    const shouldCycleHeadlines = headlines.length > 1;

    if (!shouldCycleHeadlines && isDeletingHeadline) {
      setIsDeletingHeadline(false);
      return;
    }

    const timeoutDelay = isDeletingHeadline
      ? DELETE_SPEED_MS
      : isCurrentHeadlineFullyTyped
        ? HEADLINE_PAUSE_MS
        : TYPE_SPEED_MS;

    const timeoutId = window.setTimeout(() => {
      if (isDeletingHeadline) {
        if (!isCurrentHeadlineRemoved) {
          setTypedHeadline((prev) => prev.slice(0, -1));
          return;
        }

        setIsDeletingHeadline(false);
        setActiveHeadline((prev) => (prev + 1) % headlines.length);
        return;
      }

      if (!isCurrentHeadlineFullyTyped) {
        setTypedHeadline(fullHeadline.slice(0, typedHeadline.length + 1));
        return;
      }

      if (!shouldCycleHeadlines) {
        return;
      }

      setIsDeletingHeadline(true);
    }, timeoutDelay);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [activeHeadline, typedHeadline, isDeletingHeadline, headlines]);

  return (
    <Text
      variant="web/display/xl"
      lg:variant="web/display/3xl"
      className="whitespace-pre-line text-white xl:text-[80px]/[112%]"
    >
      {typedHeadline}
      <span
        className={`ml-1 inline-block animate-pulse bg-white/95 align-middle ${
          cursorVariant === 'thin'
            ? 'h-[0.9em] w-[2px]'
            : 'h-[0.92em] w-[0.45em]'
        }`}
      />
    </Text>
  );
}
